import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Chip,
  DataTable,
  DrawerPresence,
  IconButton,
  PageHeader,
  type DataTableColumn,
  type SortState,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { dataHour } from '@app/format';
import { t, tn } from '@app/i18n';
import { useDrawerParam } from '@app/navigation';
import { ImportDrawer } from './ImportDrawer';
import { ResourceDrawer } from './ResourceDrawer';
import { ResourcesFilters } from './ResourcesFilters';
import {
  FILTERS_INICIAIS,
  hasFilter,
  useResources,
  useResourcesSummary,
  type Resource,
  type ResourcesFilterValues,
} from './Resources.api';
import { agentPath, gatewayNotReadyTip, kind as kindBadge, resourceState, risk } from './resourceVocab';
import s from './Resources.module.css';
import { useMultiSite, useSiteSlugs } from '../Sites/Sites.api';

/**
 * T6 — Resources (FR-037).
 *
 * O catálogo do que pode ser acessado. As colunas e os rótulos vêm do
 * inventário do protótipo §2.7; o risco por tool é **declarado**, nunca
 * inferido pelo nome (D4/FR-032) — tool sem risco aparece como `desconhecido` e
 * conta no indicador do cabeçalho.
 *
 * Sem campos de autenticação downstream nem referência a cofre (FR-038): quem
 * autentica no downstream é o gateway, não este console.
 *
 * FASE 2 (aim-co1.3) — AS COLUNAS DO PROTÓTIPO APROVADO EM 03/10
 *
 * Recurso · Tipo · Ligação · Site (só com mais de um) · Dono · Estado · Tools / skills. «Origem» e «Descoberta
 * via» saíram da lista e ficam no detalhe (T7): a Ligação diz o que a lista precisa saber do agente, e o Tipo mais
 * o endereço dizem o resto (§1.2 do protótipo, prancha `Main.dc.html`).
 */

/** `Última importação · {n} recursos · {m} tools sem risco` (FR-037). */
function metadata(total: number, toolsSemRisco: number, ultimaImportacao?: string | null): string {
  return [
    t('resources.list.meta.lastImport', {
      when: dataHour(ultimaImportacao) ?? t('resources.list.meta.never'),
    }),
    tn('resources.list.meta.resources', total),
    tn('resources.list.meta.toolsWithoutRisk', toolsSemRisco),
  ].join(' · ');
}

/**
 * A coluna «Ligação» (fase 2): o que a lista precisa saber de um agente. Interno: a identidade que ele é, com link
 * para o detalhe dela em Identidades (T5). Externo: o selo. Sem destino confiável: a identidade que ele perdeu.
 * MCP server e API: traço — não têm ligação.
 */
function BindingCell({ resource }: { resource: Resource }) {
  const agent = resource.agent;
  if (!agent) return <span className={s.vazio}>—</span>;
  if (agent.scope === 'external') return <Badge tone="neutral">{t('resources.binding.external')}</Badge>;
  if (agent.identity_id && agent.identity_slug) {
    return (
      <Link
        to={`/identidades?drawer=${agent.identity_id}`}
        onClick={(event) => event.stopPropagation()}
        aria-label={t('resources.binding.identity.open', { slug: agent.identity_slug })}
      >
        {t('resources.binding.identity', { slug: agent.identity_slug })}
      </Link>
    );
  }
  if (agent.lost_identity_slug) {
    return (
      <span className={s.duasLinhas}>
        <span>{t('resources.binding.lost')}</span>
        <span className={s.sub}>{t('resources.binding.lostWas', { slug: agent.lost_identity_slug })}</span>
      </span>
    );
  }
  return <span className={s.vazio}>{t('resources.binding.none')}</span>;
}

function columns(open: (id: string) => void, multiSite: boolean): DataTableColumn<Resource>[] {
  return [
    {
      key: 'name',
      header: t('resources.list.column.resource'),
      sortable: true,
      cell: (resource) => {
        // A segunda linha diz de onde o recurso vem: o endpoint de um MCP server ou de uma API, o caminho no
        // gateway de um agente (nunca o uuid — regra 2 do protótipo).
        const second = resource.agent ? agentPath(resource.agent.path) : resource.endpoint;
        return (
          <span className={s.duasLinhas}>
            <span className={s.nome}>{resource.name}</span>
            {second ? (
              <span className={s.sub} title={second}>
                {second}
              </span>
            ) : (
              <span className={s.vazio}>—</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'kind',
      header: t('resources.list.column.kind'),
      sortable: true,
      cell: (resource) => {
        const badge = kindBadge(resource.kind);
        return <Badge tone={badge.tone}>{badge.rotulo}</Badge>;
      },
    },
    {
      key: 'binding',
      header: t('resources.list.column.binding'),
      tip: t('resources.list.column.binding.tip'),
      cell: (resource) => <BindingCell resource={resource} />,
    },
    // REVELAÇÃO PROGRESSIVA (P1): a coluna «Site» só existe quando o tenant tem
    // mais de um. Com um site só ela seria uma coluna com o mesmo valor em toda
    // linha — ruído que empurra as colunas úteis para fora da tela.
    //
    // O `multi` vem do servidor (`SiteList.multi`), não de uma contagem local:
    // ver `useMultiSite`.
    ...(multiSite
      ? [
          {
            key: 'site',
            header: t('resources.list.column.site'),
            tip: t('resources.list.column.site.tip'),
            // O SLUG, e não o nome: é ele que entra no SPIFFE ID e o que o
            // operador vê no `kubectl`. O nome é livre e pode repetir.
            cell: (resource: Resource) => <Chip>{resource.site_slug}</Chip>,
          } satisfies DataTableColumn<Resource>,
        ]
      : []),
    {
      key: 'dono',
      header: t('resources.list.column.owner'),
      // A aderência acrescentou `owner` ao resource (§2.7): quem declara o risco
      // é o dono, e o catálogo sem dono é a divergência que o produto expõe.
      tip: t('resources.list.column.owner.tip'),
      cell: (resource) =>
        resource.owner ? <span>{resource.owner}</span> : <Badge tone="warning">{t('resources.list.noOwner')}</Badge>,
    },
    {
      key: 'state',
      header: t('resources.list.column.state'),
      tip: t('resources.list.column.state.tip'),
      cell: (resource) => {
        const state = resourceState(resource);
        return <Badge tone={state.tone}>{state.rotulo}</Badge>;
      },
    },
    {
      key: 'tools',
      header: t('resources.list.column.toolsSkills'),
      tip: t('resources.list.column.toolsSkills.tip'),
      cell: (resource) => {
        // Fase 2: as skills de um agente vêm do card, sem risco por skill — a autorização é pelo agente inteiro.
        if (resource.agent) {
          const skills = resource.agent.card?.skills ?? [];
          if (skills.length === 0) return <span className={s.vazio}>{t('resources.list.noSkills')}</span>;
          return (
            <span className={s.tools}>
              {skills.map((skill) => (
                <Chip key={skill.id}>{skill.name}</Chip>
              ))}
            </span>
          );
        }
        if (resource.tools.length === 0) {
          return <span className={s.vazio}>{t('resources.list.noTools')}</span>;
        }
        return (
          <span className={s.tools}>
            {resource.tools.map((tool) => {
              const risco = risk(tool.risk_displayed);
              const limit = tool.constrainable.join(', ');
              return (
                <span key={tool.id} className={s.tool}>
                  <Chip>{limit ? `${tool.gateway_name} · ${limit}` : tool.gateway_name}</Chip>
                  <Badge tone={risco.tone}>{risco.rotulo}</Badge>
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      key: 'acoes',
      header: '',
      width: '48px',
      cell: (resource) => (
        <span className={s.acoes}>
          <IconButton
            icon="arrow-right"
            label={t('resources.list.openRow', { name: resource.name })}
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              open(resource.id);
            }}
          />
        </span>
      ),
    },
  ];
}

export function ResourcesPage() {
  const [filters, setFiltros] = useState<ResourcesFilterValues>(FILTERS_INICIAIS);
  const drawer = useDrawerParam();

  const resources = useResources(filters);
  const summary = useResourcesSummary();
  // Revelação progressiva (P1): só com mais de um site a coluna e o filtro «Site»
  // aparecem.
  const multiSite = useMultiSite();
  const siteSlugs = useSiteSlugs();

  // FR-010: sem permissão o botão **não aparece** — nunca aparece desabilitado.
  const canImport = useCanOperar('previewResourceImport');
  const canCreate = useCanOperar('createResource');

  const itens = resources.data?.items ?? [];
  const total = resources.data?.total ?? 0;
  const filtrando = hasFilter(filters);
  // FR-028: enquanto o grupo do gateway não estiver pronto, importar fica desabilitado.
  const gatewayReady = summary.data?.gateway_ready !== false;

  /** Toda mudança de filtro volta para a primeira página. */
  const changeFilter = (parcial: Partial<ResourcesFilterValues>) =>
    setFiltros((current) => ({ ...current, ...parcial, page: parcial.page ?? 1 }));

  /**
   * Estado de ordenação da tabela, derivado do valor do contrato.
   *
   * Duas colunas ordenam, porque só elas têm valor no enum `sort` de
   * `listResources`: Resource (`nome`/`-nome`) e Tipo (`tipo`). **Não existe
   * `-tipo`** — então a coluna Tipo alterna entre ordenada e a ordem padrão do
   * servidor, e o `aria-sort` volta a `none` junto (a mesma decisão que T4 tomou
   * para Estado). `apim_id` fica de fora por não ter coluna própria: quem o
   * alcança é a busca, não o cabeçalho.
   */
  const tableOrder: SortState | null =
    filters.sort === 'name'
      ? { key: 'name', direction: 'asc' }
      : filters.sort === '-name'
        ? { key: 'name', direction: 'desc' }
        : filters.sort === 'kind'
          ? { key: 'kind', direction: 'asc' }
          : null;

  const changeOrder = ({ key, direction }: SortState) => {
    if (key === 'name') {
      changeFilter({ sort: direction === 'asc' ? 'name' : '-name' });
      return;
    }
    if (key === 'kind') {
      changeFilter({ sort: filters.sort === 'kind' ? null : 'kind' });
    }
  };

  const actions = (
    <>
      {canImport ? (
        <span title={gatewayReady ? undefined : gatewayNotReadyTip()}>
          <Button
            variant="out"
            icon="download"
            disabled={!gatewayReady}
            onClick={() => drawer.abrir('importar')}
          >
            {t('resources.list.import')}
          </Button>
        </span>
      ) : null}
      {canCreate ? (
        <Button icon="plus" onClick={() => drawer.abrir('novo')}>
          {t('resources.list.new')}
        </Button>
      ) : null}
    </>
  );

  return (
    <>
      <PageHeader
        title={t('resources.list.title')}
        tip={t('resources.list.purpose')}
        icon="server"
        updatedLabel={metadata(
          summary.data?.total ?? total,
          summary.data?.tools_without_risk ?? 0,
          summary.data?.last_import_at,
        )}
        actions={actions}
      />

      <Card
        title={t('resources.list.card.title')}
        subtitle={tn('resources.list.card.subtitle', total)}
        bare
      >
        <DataTable<Resource>
          label={t('resources.list.table.label')}
          columns={columns(drawer.abrir, multiSite)}
          rows={itens}
          rowKey={(resource) => resource.id}
          loading={resources.isLoading}
          error={resources.isError ? resources.error : null}
          onRetry={() => void resources.refetch()}
          sort={tableOrder}
          onSortChange={changeOrder}
          search={filters.q}
          onSearchChange={(q) => changeFilter({ q })}
          searchPlaceholder={t('resources.list.searchPlaceholder')}
          filters={
            <ResourcesFilters
              kind={filters.kind}
              onTipoChange={(kind) => changeFilter({ kind })}
              owners={summary.data?.owners ?? []}
              owner={filters.owner}
              onOwnerChange={(owner) => changeFilter({ owner })}
              semRisco={filters.semRisco}
              onSemRiscoChange={(withoutRisk) => changeFilter({ semRisco: withoutRisk })}
              scope={filters.scope}
              onScopeChange={(scope) => changeFilter({ scope })}
              // Lista vazia sem `multi`: a revelação progressiva vale para o filtro
              // como vale para a coluna — com um site só ele seria um combobox de
              // uma opção.
              sites={siteSlugs}
              site={filters.site}
              onSiteChange={(site) => changeFilter({ site })}
            />
          }
          filtersApplied={filtrando}
          // Ordenar não é filtrar: "Limpar filtros" preserva a ordem escolhida (T4).
          onClearFilters={() => setFiltros({ ...FILTERS_INICIAIS, sort: filters.sort })}
          page={filters.page}
          total={total}
          onPageChange={(page) => setFiltros((current) => ({ ...current, page }))}
          onRowClick={(resource) => drawer.abrir(resource.id)}
          emptyIcon="server"
          emptyTitle={t(
            filtrando
              ? 'resources.list.empty.filtered.title'
              : 'resources.list.empty.none.title',
          )}
          emptyMessage={t(
            filtrando
              ? 'resources.list.empty.filtered.message'
              : 'resources.list.empty.none.message',
          )}
          emptyAction={filtrando ? null : actions}
        />
      </Card>

      {/* `?drawer=novo` cria; `?drawer=<uuid>` edita; `?drawer=importar` é T8. */}
      <DrawerPresence>
        {drawer.id === 'importar' ? <ImportDrawer onClose={drawer.fechar} /> : null}
      </DrawerPresence>
      <DrawerPresence>
        {drawer.id && drawer.id !== 'importar' ? (
          <ResourceDrawer
            resourceId={drawer.id === 'novo' ? null : drawer.id}
            onClose={drawer.fechar}
          />
        ) : null}
      </DrawerPresence>
    </>
  );
}
